import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Database,
  Download,
  Upload,
  Trash2,
  Shield,
  FileJson,
  FileText,
  Archive,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  HardDrive,
  Lock,
  Eye,
  EyeOff,
} from 'lucide-react';
import { ResponsiveLayout, PageContainer, PageHeader, Card } from '@/components/layout/ResponsiveLayout';
import { Button, Input, Select, Tabs, Badge, Modal, Alert, Switch, Progress, Checkbox } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

interface ExportJob {
  id: number;
  type: 'full' | 'partial';
  format: 'json' | 'csv' | 'zip';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  downloadUrl?: string;
  expiresAt?: Date;
  createdAt: Date;
  completedAt?: Date;
  size?: number;
}

interface DataCategory {
  id: string;
  name: string;
  description: string;
  size: number;
  count: number;
  retentionDays: number;
  canDelete: boolean;
}

const formatOptions = [
  { value: 'json', label: 'JSON' },
  { value: 'csv', label: 'CSV' },
  { value: 'zip', label: 'ZIP Archive' },
];

export default function DataControls() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('export');
  const [loading, setLoading] = useState(false);
  const [exportJobs, setExportJobs] = useState<ExportJob[]>([]);
  const [dataCategories, setDataCategories] = useState<DataCategory[]>([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState('json');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [purgeCategory, setPurgeCategory] = useState<DataCategory | null>(null);

  // Retention settings
  const [retentionSettings, setRetentionSettings] = useState({
    chatHistory: 365,
    projectFiles: 180,
    auditLogs: 90,
    analytics: 30,
  });

  useEffect(() => {
    loadExportJobs();
    loadDataCategories();
  }, []);

  const loadExportJobs = async () => {
    try {
      // Mock export jobs
      setExportJobs([
        {
          id: 1,
          type: 'full',
          format: 'zip',
          status: 'completed',
          progress: 100,
          downloadUrl: '#',
          expiresAt: new Date(Date.now() + 86400000 * 7),
          createdAt: new Date(Date.now() - 3600000),
          completedAt: new Date(Date.now() - 3500000),
          size: 15728640,
        },
        {
          id: 2,
          type: 'partial',
          format: 'json',
          status: 'processing',
          progress: 65,
          createdAt: new Date(Date.now() - 600000),
        },
      ]);
    } catch (error) {
      console.error('Failed to load export jobs:', error);
    }
  };

  const loadDataCategories = async () => {
    try {
      setDataCategories([
        {
          id: 'projects',
          name: 'Projects',
          description: 'All project data including code, files, and configurations',
          size: 524288000,
          count: 12,
          retentionDays: 0,
          canDelete: true,
        },
        {
          id: 'chat_history',
          name: 'Chat History',
          description: 'Conversation history with ADELE',
          size: 104857600,
          count: 1250,
          retentionDays: 365,
          canDelete: true,
        },
        {
          id: 'generated_files',
          name: 'Generated Files',
          description: 'Files generated by ADELE (documents, images, code)',
          size: 268435456,
          count: 450,
          retentionDays: 180,
          canDelete: true,
        },
        {
          id: 'audit_logs',
          name: 'Audit Logs',
          description: 'Security and activity logs',
          size: 52428800,
          count: 5000,
          retentionDays: 90,
          canDelete: false,
        },
        {
          id: 'analytics',
          name: 'Analytics Data',
          description: 'Usage statistics and metrics',
          size: 26214400,
          count: 10000,
          retentionDays: 30,
          canDelete: true,
        },
      ]);
    } catch (error) {
      console.error('Failed to load data categories:', error);
    }
  };

  const handleExport = async () => {
    if (selectedCategories.length === 0) {
      toast.error('Please select at least one data category');
      return;
    }
    setLoading(true);
    try {
      await api.data.export({
        categories: selectedCategories,
        format: exportFormat,
      });
      toast.success('Export started. You will be notified when ready.');
      setShowExportModal(false);
      setSelectedCategories([]);
      loadExportJobs();
    } catch (error: any) {
      toast.error(error.message || 'Failed to start export');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setLoading(true);
    try {
      await api.data.deleteAccount();
      toast.success('Account deletion initiated');
      // Redirect to logout
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeCategory = async () => {
    if (!purgeCategory) return;
    setLoading(true);
    try {
      await api.data.purge(purgeCategory.id);
      toast.success(`${purgeCategory.name} data purged`);
      setShowPurgeModal(false);
      setPurgeCategory(null);
      loadDataCategories();
    } catch (error: any) {
      toast.error(error.message || 'Failed to purge data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRetention = async () => {
    setLoading(true);
    try {
      await api.data.updateRetention(retentionSettings);
      toast.success('Retention settings saved');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const totalSize = dataCategories.reduce((acc, cat) => acc + cat.size, 0);

  const tabs = [
    { id: 'export', label: 'Export Data', icon: <Download className="w-4 h-4" /> },
    { id: 'storage', label: 'Storage', icon: <HardDrive className="w-4 h-4" /> },
    { id: 'retention', label: 'Retention', icon: <Clock className="w-4 h-4" /> },
    { id: 'privacy', label: 'Privacy', icon: <Shield className="w-4 h-4" /> },
  ];

  return (
    <ResponsiveLayout>
      <PageContainer>
        <PageHeader
          title="Data Controls"
          description="Manage your data, exports, and privacy settings"
        />

        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className="mb-6" />

        {/* Export Tab */}
        {activeTab === 'export' && (
          <div className="space-y-6">
            <Card>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">Export Your Data</h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    Download a copy of your data in various formats
                  </p>
                </div>
                <Button onClick={() => setShowExportModal(true)} icon={<Download className="w-4 h-4" />}>
                  New Export
                </Button>
              </div>

              {exportJobs.length === 0 ? (
                <div className="text-center py-8">
                  <Archive className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">No export jobs yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {exportJobs.map((job) => (
                    <div
                      key={job.id}
                      className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-zinc-700 rounded-lg">
                          {job.format === 'json' && <FileJson className="w-5 h-5 text-yellow-400" />}
                          {job.format === 'csv' && <FileText className="w-5 h-5 text-green-400" />}
                          {job.format === 'zip' && <Archive className="w-5 h-5 text-blue-400" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">
                              {job.type === 'full' ? 'Full Export' : 'Partial Export'}
                            </span>
                            <Badge
                              variant={
                                job.status === 'completed'
                                  ? 'success'
                                  : job.status === 'processing'
                                  ? 'info'
                                  : job.status === 'failed'
                                  ? 'error'
                                  : 'default'
                              }
                            >
                              {job.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-zinc-400 mt-1">
                            {job.status === 'completed' && job.size && (
                              <span>{formatBytes(job.size)} • </span>
                            )}
                            Created {job.createdAt.toLocaleString()}
                            {job.expiresAt && (
                              <span> • Expires {job.expiresAt.toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {job.status === 'processing' && (
                          <div className="w-32">
                            <Progress value={job.progress} max={100} />
                          </div>
                        )}
                        {job.status === 'completed' && job.downloadUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(job.downloadUrl, '_blank')}
                            icon={<Download className="w-4 h-4" />}
                          >
                            Download
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Storage Tab */}
        {activeTab === 'storage' && (
          <div className="space-y-6">
            <Card>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white">Storage Usage</h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    Total: {formatBytes(totalSize)}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {dataCategories.map((category) => (
                  <div
                    key={category.id}
                    className="p-4 bg-zinc-800/50 rounded-xl"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-white">{category.name}</h4>
                        <p className="text-sm text-zinc-400">{category.description}</p>
                      </div>
                      {category.canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPurgeCategory(category);
                            setShowPurgeModal(true);
                          }}
                          icon={<Trash2 className="w-4 h-4 text-red-400" />}
                        >
                          Purge
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm text-zinc-500 mb-2">
                      <span>{category.count.toLocaleString()} items</span>
                      <span>{formatBytes(category.size)}</span>
                    </div>
                    <Progress
                      value={category.size}
                      max={totalSize}
                      className="h-2"
                    />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Retention Tab */}
        {activeTab === 'retention' && (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-white mb-6">Data Retention Settings</h3>
              <p className="text-sm text-zinc-400 mb-6">
                Configure how long different types of data are retained before automatic deletion.
              </p>

              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Select
                    label="Chat History"
                    options={[
                      { value: '30', label: '30 days' },
                      { value: '90', label: '90 days' },
                      { value: '180', label: '180 days' },
                      { value: '365', label: '1 year' },
                      { value: '0', label: 'Never delete' },
                    ]}
                    value={retentionSettings.chatHistory.toString()}
                    onChange={(value) =>
                      setRetentionSettings({ ...retentionSettings, chatHistory: parseInt(value) })
                    }
                  />
                  <Select
                    label="Project Files"
                    options={[
                      { value: '30', label: '30 days' },
                      { value: '90', label: '90 days' },
                      { value: '180', label: '180 days' },
                      { value: '365', label: '1 year' },
                      { value: '0', label: 'Never delete' },
                    ]}
                    value={retentionSettings.projectFiles.toString()}
                    onChange={(value) =>
                      setRetentionSettings({ ...retentionSettings, projectFiles: parseInt(value) })
                    }
                  />
                  <Select
                    label="Audit Logs"
                    options={[
                      { value: '30', label: '30 days' },
                      { value: '90', label: '90 days' },
                      { value: '180', label: '180 days' },
                      { value: '365', label: '1 year' },
                    ]}
                    value={retentionSettings.auditLogs.toString()}
                    onChange={(value) =>
                      setRetentionSettings({ ...retentionSettings, auditLogs: parseInt(value) })
                    }
                  />
                  <Select
                    label="Analytics Data"
                    options={[
                      { value: '7', label: '7 days' },
                      { value: '30', label: '30 days' },
                      { value: '90', label: '90 days' },
                      { value: '180', label: '180 days' },
                    ]}
                    value={retentionSettings.analytics.toString()}
                    onChange={(value) =>
                      setRetentionSettings({ ...retentionSettings, analytics: parseInt(value) })
                    }
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveRetention} loading={loading}>
                    Save Settings
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Privacy Tab */}
        {activeTab === 'privacy' && (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-white mb-6">Privacy Settings</h3>

              <div className="space-y-4">
                <Switch
                  checked={true}
                  onChange={() => {}}
                  label="Data Encryption"
                  description="All your data is encrypted at rest and in transit"
                  disabled
                />
                <Switch
                  checked={false}
                  onChange={() => {}}
                  label="Analytics Opt-out"
                  description="Opt out of anonymous usage analytics"
                />
                <Switch
                  checked={true}
                  onChange={() => {}}
                  label="AI Training Opt-out"
                  description="Your data will not be used to train AI models"
                />
              </div>
            </Card>

            <Card className="border-red-500/20">
              <h3 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
              <Alert variant="warning" className="mb-4">
                These actions are irreversible. Please proceed with caution.
              </Alert>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl">
                  <div>
                    <h4 className="font-medium text-white">Delete All Data</h4>
                    <p className="text-sm text-zinc-400">
                      Permanently delete all your data while keeping your account
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedCategories(dataCategories.map((c) => c.id));
                      setShowExportModal(true);
                    }}
                  >
                    Export First
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-xl">
                  <div>
                    <h4 className="font-medium text-white">Delete Account</h4>
                    <p className="text-sm text-zinc-400">
                      Permanently delete your account and all associated data
                    </p>
                  </div>
                  <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                    Delete Account
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Export Modal */}
        <Modal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Export Data"
          size="lg"
        >
          <div className="space-y-4">
            <p className="text-zinc-400">
              Select the data categories you want to export:
            </p>

            <div className="space-y-2">
              {dataCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                    selectedCategories.includes(category.id)
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-white">{category.name}</h4>
                      <p className="text-sm text-zinc-400">{category.description}</p>
                    </div>
                    <Checkbox
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => toggleCategory(category.id)}
                    />
                  </div>
                </div>
              ))}
            </div>

            <Select
              label="Export Format"
              options={formatOptions}
              value={exportFormat}
              onChange={setExportFormat}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowExportModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleExport} loading={loading}>
                Start Export
              </Button>
            </div>
          </div>
        </Modal>

        {/* Purge Modal */}
        <Modal
          isOpen={showPurgeModal}
          onClose={() => setShowPurgeModal(false)}
          title={`Purge ${purgeCategory?.name}`}
        >
          <div className="space-y-4">
            <Alert variant="warning">
              This will permanently delete all {purgeCategory?.name.toLowerCase()} data.
              This action cannot be undone.
            </Alert>

            <div className="p-4 bg-zinc-800 rounded-xl">
              <p className="text-sm text-zinc-400">
                <strong className="text-white">{purgeCategory?.count.toLocaleString()}</strong> items
                ({formatBytes(purgeCategory?.size || 0)}) will be deleted.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowPurgeModal(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handlePurgeCategory} loading={loading}>
                Purge Data
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Account Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Account"
        >
          <div className="space-y-4">
            <Alert variant="error">
              <AlertTriangle className="w-5 h-5" />
              <div>
                <p className="font-medium">This action is permanent</p>
                <p className="text-sm mt-1">
                  Your account and all data will be permanently deleted. This cannot be undone.
                </p>
              </div>
            </Alert>

            <Input
              label="Type DELETE to confirm"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="DELETE"
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAccount}
                loading={loading}
                disabled={deleteConfirmation !== 'DELETE'}
              >
                Delete My Account
              </Button>
            </div>
          </div>
        </Modal>
      </PageContainer>
    </ResponsiveLayout>
  );
}
